import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getImageUrl } from '../../services/tmdb';
import { getAutoPlaySettings } from '../../services/autoplay';
import type { Movie, TvShow } from '../../types';

interface UpNextOverlayProps {
  nextMedia: Movie | TvShow | null;
  type: 'movie' | 'tv';
  reason?: string;
  // For TV: next episode info
  nextSeason?: number;
  nextEpisode?: number;
  episodeTitle?: string;
  // Callbacks
  onPlay: () => void;
  onCancel: () => void;
  // Optional: auto-started
  autoStart?: boolean;
}

export default function UpNextOverlay({
  nextMedia,
  type,
  reason,
  nextSeason,
  nextEpisode,
  episodeTitle,
  onPlay,
  onCancel,
  autoStart = true,
}: UpNextOverlayProps) {
  const settings = getAutoPlaySettings();
  const [countdown, setCountdown] = useState(settings.countdownSeconds);
  const [isPaused, setIsPaused] = useState(false);

  // Countdown timer
  useEffect(() => {
    if (!autoStart || !settings.enabled || isPaused) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [autoStart, settings.enabled, isPaused]);

  // Trigger play when countdown reaches 0
  useEffect(() => {
    if (countdown === 0 && autoStart && settings.enabled && !isPaused) {
      onPlay();
    }
  }, [countdown, autoStart, settings.enabled, isPaused, onPlay]);

  if (!nextMedia) return null;

  const title = type === 'movie' || !nextEpisode
    ? ('title' in nextMedia ? nextMedia.title : nextMedia.name)
    : `S${nextSeason} E${nextEpisode}`;

  const subtitle = type === 'tv' && episodeTitle
    ? episodeTitle
    : type === 'movie'
    ? ('title' in nextMedia ? nextMedia.release_date?.split('-')[0] : '')
    : '';

  const posterPath = nextMedia.poster_path || nextMedia.backdrop_path;

  return (
    <div className="absolute inset-0 bg-black/80 z-40 flex items-center justify-center animate-fade-in">
      <div className="max-w-lg w-full mx-4 bg-zinc-900 rounded-xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="px-4 py-3 bg-white/5 border-b border-white/10">
          <span className="text-sm font-medium text-gray-400">
            {type === 'tv' ? 'NEXT EPISODE' : 'UP NEXT'}
          </span>
        </div>

        {/* Content */}
        <div className="p-4 flex gap-4">
          {/* Poster */}
          <div className="w-24 h-36 flex-shrink-0 rounded-lg overflow-hidden bg-zinc-800">
            {posterPath ? (
              <img
                src={getImageUrl(posterPath, 'w185')}
                alt={title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-3xl">
                🎬
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold truncate">{title}</h3>
            {subtitle && <p className="text-sm text-gray-400 mb-2">{subtitle}</p>}

            {reason && (
              <p className="text-sm text-[var(--color-primary)] mb-3 line-clamp-2">
                💡 {reason}
              </p>
            )}

            {/* Progress bar */}
            <div className="mt-4">
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[var(--color-primary)] transition-all duration-1000"
                  style={{
                    width: `${((settings.countdownSeconds - countdown) / settings.countdownSeconds) * 100}%`,
                  }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {settings.enabled && !isPaused
                  ? `Starting in ${countdown}s`
                  : 'Auto-play paused'}
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-4 pb-4 flex gap-3">
          <button
            onClick={onPlay}
            className="flex-1 py-3 bg-white text-black font-semibold rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
            </svg>
            Play Now
          </button>

          <button
            onClick={() => setIsPaused(!isPaused)}
            className="px-4 py-3 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
            title={isPaused ? 'Resume countdown' : 'Pause countdown'}
          >
            {isPaused ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            )}
          </button>

          <button
            onClick={onCancel}
            className="px-4 py-3 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
            title="Cancel"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* More Info Link */}
        <div className="px-4 pb-4">
          <Link
            to={`/${type}/${nextMedia.id}`}
            className="text-sm text-gray-400 hover:text-white flex items-center gap-1 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            More Info
          </Link>
        </div>
      </div>
    </div>
  );
}
